import type { AxiosError } from 'axios';
import axios from 'axios';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { UserRole } from '../../../enum';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { fetchClinicianSites } from '../admin-clinicians/adminClinicianSlice';
import type { Status } from '../client-profile/client-tabs/graphs/graphSlice';
import type {
  CheckIn,
  ClientEngagementDetail,
  ClientNoNotification,
  ClientNotOnboarded,
  NewClient,
  TotalClientDetail,
} from './modals/modalDashboardSlice';
import { dateTimeFilterFormat } from './program-dashboard.helper';

const { logError } = RaygunErrorHandlerService();

export type DashboardRequestParams = {
  app_code: string;
  end_date: Dayjs;
  start_date: Dayjs;
  site_id?: string;
  organization_id?: string;
};

export type NameValue = {
  name: string;
  value: number;
};

export type NameTotal<
  K extends string = 'age_group' | 'primary_diagnosis' | 'gender' | 'product' | 'military_association',
> = Record<K, string> & {
  total: number;
};

export type DataType = 'bar' | 'line' | 'category';

export class BarLineData {
  public name: string | null;
  public type: DataType;
  public data: (string | number)[];
  public stack?: string;

  constructor(name: string | null, type: DataType, data: (string | number)[]) {
    this.name = name;
    this.type = type;
    this.data = data;
  }
}

export const InterventionType = {
  HighSeverity: 'High severity',
  MediumSeverity: 'Medium severity',
  LowSeverity: 'Low severity',
  FeedbackOnly: 'Feedback only',
} as const;

export type InterventionValue = (typeof InterventionType)[keyof typeof InterventionType];

export type Intervention = {
  intervention_id: string;
  client_name: string;
  sites: string[];
  clinician_name: string;
  intervention_type: InterventionValue;
  date: string;
};

export type TimeInStatus = {
  client_id: string;
  full_name: string;
  sites: string[];
  products: string[];
  cumulative_minutes_in_status: Record<Status, number>;
};

export type DashboardMoods = {
  count: number;
  mood:
    | 'Angry'
    | 'Bored'
    | 'Content'
    | 'Good'
    | 'Grateful'
    | 'Grounded'
    | 'Guilty'
    | 'Lonely'
    | 'Proud'
    | 'Sad'
    | 'Tired'
    | 'Worried'
    | 'Other';
  mood_id: string;
};

export type MostCommonWordCount = {
  word: string;
  frequency: number;
};

type ClientEngagementData = {
  client_engagement: number | null;
  client_engagement_details: ClientEngagementDetail[];
};

export type PopulationData = {
  new_clients: NewClient[] | null;
  clients_who_did_not_onboard: ClientNotOnboarded[] | null;
  caf_rcmp_distribution: NameTotal<'military_association'>[];
  product_distribution: NameValue[];
  gender_distribution: NameTotal<'gender'>[];
  age_distribution: NameTotal<'age_group'>[];
  primary_diagnosis_distribution: NameTotal<'primary_diagnosis'>[];
};

type ProgramDashboardData = {
  previousParams: Partial<DashboardRequestParams>;
  issues: {
    number_of_clients_with_no_activity: number | null;
    clients_with_no_devices: ClientNoNotification[] | null;
  };
  check_in_distribution_by_dow_hour: [number, number, number][];
  client_activity_trends: {
    sentiment_counts: NameValue[];
    interventions: Intervention[];
    status_distribution: TimeInStatus[];
    mood_counts: DashboardMoods[];
    most_common_word_counts: MostCommonWordCount[];
  };
};

type ProgramDashboardSlideType = {
  programDashboardParams: DashboardRequestParams;
  programDashboardData: ProgramDashboardData;
  loadingDashboard: boolean;
  clientEngagementData: ClientEngagementData;
  loadingClientEngagement: boolean;
  populationData: PopulationData;
  loadingPopulation: boolean;
  check_ins: CheckIn[] | null;
  loadingCheckIn: boolean;
  total_clients_details: TotalClientDetail[] | null;
  loadingTotalClient: boolean;
};

const initialState: ProgramDashboardSlideType = {
  programDashboardParams: {
    app_code: 'ta',
    start_date: dayjs().subtract(1, 'month'),
    end_date: dayjs(),
  },
  programDashboardData: {
    previousParams: {},
    issues: {
      number_of_clients_with_no_activity: null,
      clients_with_no_devices: null,
    },
    check_in_distribution_by_dow_hour: [],
    client_activity_trends: {
      sentiment_counts: [],
      interventions: [],
      status_distribution: [],
      mood_counts: [],
      most_common_word_counts: [],
    },
  },
  clientEngagementData: {
    client_engagement: null,
    client_engagement_details: [],
  },
  populationData: {
    new_clients: null,
    clients_who_did_not_onboard: null,
    product_distribution: [],
    gender_distribution: [],
    caf_rcmp_distribution: [],
    age_distribution: [],
    primary_diagnosis_distribution: [],
  },
  loadingDashboard: false,
  loadingClientEngagement: false,
  loadingPopulation: false,
  loadingCheckIn: false,
  loadingTotalClient: false,
  check_ins: null,
  total_clients_details: [],
};

export const loadDashboard = createAsyncThunk<ProgramDashboardData, undefined, AsyncThunkConfig>(
  'programDashboard/loadDashboard',
  async (_, thunkAPI) => {
    try {
      const currentUser = thunkAPI.getState().authSlice.currentUser;

      if (currentUser.role === UserRole.SITE && currentUser.user_id) {
        await thunkAPI.dispatch(fetchClinicianSites(currentUser.user_id));
        const site_id = thunkAPI.getState().adminClinicianSlice.clinicianSites[0]?.site_id;
        if (site_id) {
          thunkAPI.dispatch(handleParamsChange({ site_id }));
        }
      }

      const programDashboardParams = thunkAPI.getState().programDashboardSlice.programDashboardParams;
      const body = {
        ...programDashboardParams,
        start_date: programDashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: programDashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        site_id: programDashboardParams.site_id || null,
        organization_id: programDashboardParams.organization_id || null,
      };

      const response = await axios.post('v3_program_dashboard', body, { signal: thunkAPI.signal });
      return (response as unknown as ProgramDashboardData) ?? initialState.programDashboardData;
    } catch (e) {
      logError(e, ['programDashboardSlice', 'loadDashboard']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const loadClientEngagement = createAsyncThunk<ClientEngagementData, undefined, AsyncThunkConfig>(
  'programDashboard/loadClientEngagement',
  async (_, thunkAPI) => {
    try {
      const programDashboardParams = thunkAPI.getState().programDashboardSlice.programDashboardParams;
      const body = {
        ...programDashboardParams,
        start_date: programDashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: programDashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        site_id: programDashboardParams.site_id || null,
        organization_id: programDashboardParams.organization_id || null,
      };

      const response = await axios.post('v3_get_client_engagement_details', body, { signal: thunkAPI.signal });
      return (response as unknown as ClientEngagementData) ?? initialState.programDashboardData;
    } catch (e) {
      logError(e, ['programDashboardSlice', 'loadClientEngagement']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const loadPopulationDetails = createAsyncThunk<PopulationData, undefined, AsyncThunkConfig>(
  'programDashboard/loadPopulationDetails',
  async (_, thunkAPI) => {
    try {
      const programDashboardParams = thunkAPI.getState().programDashboardSlice.programDashboardParams;
      const body = {
        ...programDashboardParams,
        start_date: programDashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: programDashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        site_id: programDashboardParams.site_id || null,
        organization_id: programDashboardParams.organization_id || null,
      };

      const response = await axios.post('v3_population_details', body, { signal: thunkAPI.signal });
      return (response as unknown as PopulationData) ?? initialState.programDashboardData;
    } catch (e) {
      logError(e, ['programDashboardSlice', 'loadPopulationDetails']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const loadTotalClientDetails = createAsyncThunk<TotalClientDetail[], undefined, AsyncThunkConfig>(
  'programDashboard/loadTotalClientDetails',
  async (_, thunkAPI) => {
    try {
      const programDashboardParams = thunkAPI.getState().programDashboardSlice.programDashboardParams;
      const body = {
        ...programDashboardParams,
        start_date: programDashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: programDashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        site_id: programDashboardParams.site_id || null,
        organization_id: programDashboardParams.organization_id || null,
      };

      const response = await axios.post('v3_all_client_data', body, { signal: thunkAPI.signal });
      return (response as unknown as TotalClientDetail[]) ?? initialState.programDashboardData;
    } catch (e) {
      logError(e, ['programDashboardSlice', 'loadTotalClientDetails']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const loadCheckInDetails = createAsyncThunk<CheckIn[], undefined, AsyncThunkConfig>(
  'programDashboard/loadCheckInDetails',
  async (_, thunkAPI) => {
    try {
      const {
        programDashboardSlice: { programDashboardParams },
        authSlice: { currentUser },
      } = thunkAPI.getState();
      const body = {
        ...programDashboardParams,
        start_date: programDashboardParams.start_date.startOf('day').format(dateTimeFilterFormat),
        end_date: programDashboardParams.end_date.endOf('day').format(dateTimeFilterFormat),
        site_id: programDashboardParams.site_id || null,
        organization_id: programDashboardParams.organization_id || null,
      };
      if (currentUser.role === UserRole.ADMIN) {
        body.organization_id = programDashboardParams.organization_id || null;
      }

      const response = await axios.post('v3_check_in_data', body, { signal: thunkAPI.signal });
      return (response as unknown as CheckIn[]) ?? initialState.programDashboardData;
    } catch (e) {
      logError(e, ['programDashboardSlice', 'loadCheckInDetails']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const programDashboardSlice = createSlice({
  name: 'programDashboard',
  initialState,
  reducers: {
    handleParamsChange: (state, action: PayloadAction<Partial<DashboardRequestParams>>) => {
      state.programDashboardParams = { ...state.programDashboardParams, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDashboard.pending, (state, _action) => {
        state.loadingDashboard = true;
      })
      .addCase(loadDashboard.fulfilled, (state, action) => {
        state.programDashboardData = action.payload;
        state.programDashboardData.previousParams = {
          site_id: state.programDashboardParams.site_id,
          start_date: state.programDashboardParams.start_date,
          end_date: state.programDashboardParams.end_date,
          organization_id: state.programDashboardParams.organization_id,
        };
        state.loadingDashboard = false;
      })
      .addCase(loadDashboard.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingDashboard = false;
        }
      })
      .addCase(loadClientEngagement.pending, (state, _action) => {
        state.loadingClientEngagement = true;
      })
      .addCase(loadClientEngagement.fulfilled, (state, action) => {
        state.clientEngagementData = action.payload;
        state.loadingClientEngagement = false;
      })
      .addCase(loadClientEngagement.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingClientEngagement = false;
        }
      })
      .addCase(loadPopulationDetails.pending, (state, _action) => {
        state.loadingPopulation = true;
      })
      .addCase(loadPopulationDetails.fulfilled, (state, action) => {
        state.populationData = action.payload;
        state.loadingPopulation = false;
      })
      .addCase(loadPopulationDetails.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingPopulation = false;
        }
      })
      .addCase(loadTotalClientDetails.pending, (state, _action) => {
        state.loadingTotalClient = true;
      })
      .addCase(loadTotalClientDetails.fulfilled, (state, action) => {
        state.total_clients_details = action.payload;
        state.loadingTotalClient = false;
      })
      .addCase(loadTotalClientDetails.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingTotalClient = false;
        }
      })
      .addCase(loadCheckInDetails.pending, (state, _action) => {
        state.loadingCheckIn = true;
      })
      .addCase(loadCheckInDetails.fulfilled, (state, action) => {
        state.check_ins = action.payload;
        state.loadingCheckIn = false;
      })
      .addCase(loadCheckInDetails.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingCheckIn = false;
        }
      });
  },
});

export const { handleParamsChange } = programDashboardSlice.actions;
